import * as Sentry from '@sentry/react';
import { Dispatch, useCallback, useRef } from 'react';
import { useAppDispatch } from 'store/hooks';
import { roomJoinRequest } from 'features/room/actions';
import { logger } from 'utils/logger';
import { JoinPublisherActionType } from 'utils/broadcast-setup/reducer';
import { EnumeratedMediaDevices } from 'features/user-media/types';
import { isDeviceGivenPermission } from 'features/user-media/utils';
import {
  PendingUserMediaFlow,
  PendingUserMediaWatchdog,
  watchPendingUserMedia,
} from 'utils/broadcast-setup/pendingUserMediaWatchdog';

const SENTRY_FLUSH_TIMEOUT_MS = 1000;

// No unmount cleanup on purpose: the browser request outlives the component, and StrictMode's
// dev-only effect cycle would otherwise dispose the watchdog before it ever fires.
export const usePendingUserMediaWatchdog = (
  dispatch: Dispatch<JoinPublisherActionType>,
  mediaDevices: EnumeratedMediaDevices
) => {
  const appDispatch = useAppDispatch();
  const watchdogRef = useRef<PendingUserMediaWatchdog<unknown> | null>(null);

  const observe = <T>(request: Promise<T>, flow: PendingUserMediaFlow) => {
    const watchdog = watchPendingUserMedia(request, {
      flow,
      labelsAtStartup:
        isDeviceGivenPermission(mediaDevices, 'audioinput') ||
        isDeviceGivenPermission(mediaDevices, 'videoinput'),
      onStalled: () => dispatch({ type: 'statusUpdated', payload: 'stalled' }),
      onRecovered: () => dispatch({ type: 'statusUpdated', payload: 'prompt' }),
    });

    watchdogRef.current = watchdog;

    return watchdog.settled;
  };

  // The tap is itself the autoplay gesture, so join at once; 'prompt' brings the loader back
  // (and AwaitingRoomJoin if the join lands in the lobby) while the room comes up.
  const joinWithoutMedia = useCallback(() => {
    watchdogRef.current?.abandon('join-without-media');
    dispatch({ type: 'statusUpdated', payload: 'prompt' });
    appDispatch(roomJoinRequest());
  }, [dispatch, appDispatch]);

  // Neither remote sink drains itself before the document goes: the log tail runs on a timer
  // and the Sentry SDK batches logs with no unload hook.
  const reload = useCallback(async () => {
    watchdogRef.current?.abandon('reload');
    logger.flush();
    await Sentry.flush(SENTRY_FLUSH_TIMEOUT_MS);
    window.location.reload();
  }, []);

  return { observe, joinWithoutMedia, reload };
};
